import React, { createContext, useContext, useState, useEffect } from 'react';

const getEnv = (key) => {
  if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
    return window._env_[key];
  }
  return import.meta.env[key];
};

// URL do painel administrativo (fallback)
const DEFAULT_PAINEL_URL = getEnv('VITE_PAINEL_URL')

// Contexto de autenticação para o dashboard
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [painelUrl, setPainelUrl] = useState(DEFAULT_PAINEL_URL);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = () => {
    try {
      // Primeiro, verificar se há dados na URL (vindo do painel)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      const userNameFromUrl = urlParams.get('userName');
      const userEmailFromUrl = urlParams.get('userEmail');
      const userRoleFromUrl = urlParams.get('userRole');
      const userIdFromUrl = urlParams.get('userId');
      const painelUrlFromUrl = urlParams.get('painelUrl');

      if (tokenFromUrl) {
        // Salvar token no localStorage
        localStorage.setItem('authToken', tokenFromUrl);
        
        // Se veio dados do usuário, salvar também
        if (userNameFromUrl || userEmailFromUrl) {
          const userData = {
            id: userIdFromUrl || '',
            name: userNameFromUrl || '',
            email: userEmailFromUrl || '',
            role: userRoleFromUrl || 'user'
          };
          localStorage.setItem('dashboardUser', JSON.stringify(userData));
          setUser(userData);
        }

        // Salvar URL do painel se veio
        if (painelUrlFromUrl) {
          localStorage.setItem('painelUrl', painelUrlFromUrl);
          setPainelUrl(painelUrlFromUrl);
        }

        // Limpar parâmetros da URL (segurança)
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      // Se não veio da URL, verificar localStorage
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('dashboardUser');
      const savedPainelUrl = localStorage.getItem('painelUrl');

      if (token) {
        if (savedUser) {
          try {
            setUser(JSON.parse(savedUser));
          } catch (e) {
            // JSON inválido, ignorar
          }
        }
        if (savedPainelUrl) {
          setPainelUrl(savedPainelUrl);
        }
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Erro ao inicializar autenticação:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para obter iniciais do nome
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase();
    }
    return user.name.charAt(0).toUpperCase();
  };

  // Função para fazer logout e voltar ao painel
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('dashboardUser');
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = `${painelUrl}/login`;
  };

  // Função para voltar ao painel (sem logout)
  const goToPanel = () => {
    window.location.href = painelUrl;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    painelUrl,
    getUserInitials,
    logout,
    goToPanel
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export default AuthContext;

